import { CONFIG } from './config.js';
import { rotuloConstrutora, rotuloDiferencial, precoCurto, comoLista } from './formato.js';

export const VENDIDO = '100% vendido';

export const ESTADO_VAZIO = Object.freeze({
  busca: '',
  cidades: [], bairros: [], tipos: [], construtoras: [], diferenciais: [],
  precoMin: null, precoMax: null, quartos: null, anoEntrega: null,
  // Metade do catálogo do portal está esgotada. Mostrar tudo faria o lead
  // topar com imóvel indisponível na metade das vezes, então o padrão é
  // esconder — e quem quiser ver liga o filtro.
  mostrarVendidos: false,
});

// O bloco de marcas combinantes (U+0300 a U+036F) e escrito com escapes, e
// nao com os caracteres literais: marca combinante solta em codigo-fonte e
// invisivel no editor e some em copiar-e-colar.
const semAcento = (s) =>
  String(s ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

export function aplicaFiltros(features, e) {
  const busca = semAcento(e.busca);
  return features.filter((f) => {
    const p = f.properties;

    if (!e.mostrarVendidos && p.status === VENDIDO) return false;

    if (e.cidades.length && !e.cidades.includes(p.cidade)) return false;
    if (e.bairros.length && !e.bairros.includes(p.bairro)) return false;
    if (e.tipos.length && !e.tipos.includes(p.tipo)) return false;
    if (e.construtoras.length && !e.construtoras.includes(p.construtora)) return false;

    if (e.precoMin != null || e.precoMax != null) {
      // Sem preço não dá para afirmar que cabe na faixa: fica de fora.
      if (p.precoMin == null) return false;
      if (e.precoMin != null && p.precoMin < e.precoMin) return false;
      if (e.precoMax != null && p.precoMin > e.precoMax) return false;
    }

    if (e.quartos != null) {
      const min = p.quartosMin ?? p.quartosMax;
      const max = p.quartosMax ?? p.quartosMin;
      if (min == null) return false;
      // No topo da escala o filtro vira "pelo menos"
      const cabe = e.quartos >= 4 ? max >= e.quartos : e.quartos >= min && e.quartos <= max;
      if (!cabe) return false;
    }

    if (e.anoEntrega != null) {
      if (!p.entrega) return false;
      if (Number(String(p.entrega).slice(0, 4)) !== e.anoEntrega) return false;
    }

    if (e.diferenciais.length) {
      const tem = new Set(comoLista(p.diferenciaisTop));
      if (!e.diferenciais.every((d) => tem.has(d))) return false;
    }

    if (busca) {
      const alvo = semAcento([p.titulo, p.bairro, p.cidade, p.construtora].join(' '));
      if (!alvo.includes(busca)) return false;
    }
    return true;
  });
}

const LISTAS = ['cidades', 'bairros', 'tipos', 'construtoras', 'diferenciais'];
const NUMEROS = ['precoMin', 'precoMax', 'quartos', 'anoEntrega'];

export function paraUrl(e) {
  const q = new URLSearchParams();
  if (e.mostrarVendidos) q.set('vendidos', '1');
  for (const k of LISTAS) if (e[k]?.length) q.set(k, e[k].join('|'));
  for (const k of NUMEROS) if (e[k] != null) q.set(k, String(e[k]));
  if (e.busca) q.set('busca', e.busca);
  const s = q.toString();
  return s ? `?${s}` : '';
}

/**
 * URL completa do estado, sempre a partir do caminho absoluto.
 *
 * Montar so com a query nao serve: `history.replaceState(null, '', '')`
 * MANTEM a URL atual -- string vazia resolve para ela mesma --, entao voltar
 * ao estado sem filtro nenhum deixava o `?vendidos=1` antigo na barra. Com so
 * o fragmento (`#imovel=x`) da no mesmo: a query anterior sobrevive. Efeito: o
 * link que o SDR copia abre um recorte diferente do que esta na tela.
 */
export function urlDoEstado(estado, caminho, fragmento = '') {
  return caminho + paraUrl(estado) + fragmento;
}

export function daUrl(texto) {
  const q = new URLSearchParams(String(texto ?? '').replace(/^\?/, ''));
  const e = { ...ESTADO_VAZIO };
  for (const k of LISTAS) {
    const v = q.get(k);
    e[k] = v ? v.split('|').filter(Boolean) : [];
  }
  for (const k of NUMEROS) {
    const bruto = q.get(k);
    const n = Number(bruto);
    e[k] = bruto !== null && bruto !== '' && Number.isFinite(n) ? n : null;
  }
  e.busca = q.get('busca') ?? '';
  e.mostrarVendidos = q.get('vendidos') === '1';
  return e;
}

/** As opções dos filtros saem dos próprios dados, nunca de lista fixa. */
export function opcoesDe(features) {
  const p = (f) => f.properties;
  const conjunto = (fn) => [...new Set(features.map(fn).filter(Boolean))].sort();
  return {
    cidades: conjunto((f) => p(f).cidade),
    bairros: conjunto((f) => p(f).bairro),
    tipos: conjunto((f) => p(f).tipo),
    construtoras: conjunto((f) => p(f).construtora),
    diferenciais: [...new Set(features.flatMap((f) => comoLista(p(f).diferenciaisTop)))].sort(),
    vendidos: features.filter((f) => p(f).status === VENDIDO).length,
    // Arredondado PARA CIMA ate cair na grade do slider. Sem isso o topo da
    // faixa fica inalcancavel: o preco mais caro do catalogo era R$ 17.205.123,
    // o navegador truncava o valor do range para 17.200.000 -- abaixo do max --
    // e o codigo lia esse valor truncado como filtro de preco ativo. Efeito:
    // qualquer mexida em qualquer filtro sumia em silencio com todo imovel sem
    // preco (176 vendidos e 4 a venda) e com o proprio imovel mais caro.
    precoMax: teto(Math.max(0, ...features.map((f) => p(f).precoMin ?? 0))),
    anos: [...new Set(
      features
        .map((f) => (p(f).entrega ? Number(String(p(f).entrega).slice(0, 4)) : null))
        .filter(Boolean)
    )].sort(),
  };
}

/**
 * Grade do slider de preco. O `min`, o `step` e o teto de `opcoesDe` precisam
 * concordar: um `max` fora da grade e truncado pelo navegador e vira filtro
 * fantasma.
 */
export const PRECO_PISO = 100000;
export const PRECO_PASSO = 50000;

const teto = (v) =>
  v <= PRECO_PISO
    ? PRECO_PISO
    : PRECO_PISO + Math.ceil((v - PRECO_PISO) / PRECO_PASSO) * PRECO_PASSO;

export function contaAtivos(e) {
  return LISTAS.reduce((n, k) => n + e[k].length, 0)
    + NUMEROS.filter((k) => e[k] != null).length
    + (e.mostrarVendidos ? 1 : 0);
}

/** Quantos imóveis o filtro de vendidos está escondendo agora. */
export function contaVendidos(features) {
  return features.filter((f) => f.properties.status === VENDIDO).length;
}

const rotuloDe = (campo, v) => {
  if (campo === 'construtoras') return rotuloConstrutora(v);
  if (campo === 'diferenciais') return rotuloDiferencial(v);
  if (campo === 'tipos') return CONFIG.rotuloTipo[v] ?? v;
  return v;
};

const grupoChips = (campo, valores, rotulo, ativos) => `
  <fieldset class="grupo">
    <legend>${rotulo}</legend>
    <div class="chips">
      ${valores.map((v) => `
        <label class="chip">
          <input type="checkbox" name="${campo}" value="${v}" ${ativos.includes(v) ? 'checked' : ''}>
          ${rotuloDe(campo, v)}
        </label>`).join('')}
    </div>
  </fieldset>`;

export function criaPainelFiltros(elemento, opcoes, aoMudar) {
  function leElemento(busca) {
    const novo = { ...ESTADO_VAZIO, busca };
    for (const campo of LISTAS) {
      novo[campo] = [...elemento.querySelectorAll(`input[name="${campo}"]:checked`)]
        .map((i) => i.value);
    }
    const q = elemento.querySelector('input[name="quartos"]:checked');
    novo.quartos = q ? Number(q.value) : null;
    const a = elemento.querySelector('input[name="anoEntrega"]:checked');
    novo.anoEntrega = a ? Number(a.value) : null;
    const p = elemento.querySelector('input[name="precoMax"]');
    novo.precoMax = p && Number(p.value) < opcoes.precoMax ? Number(p.value) : null;
    if (novo.precoMax != null && novo.precoMax >= opcoes.precoMax) novo.precoMax = null;
    novo.mostrarVendidos = !!elemento.querySelector('input[name="mostrarVendidos"]:checked');
    return novo;
  }

  function desenha(estado) {
    elemento.innerHTML = `
      ${grupoChips('cidades', opcoes.cidades, 'Cidade', estado.cidades)}
      ${grupoChips('tipos', opcoes.tipos, 'Tipo', estado.tipos)}
      <fieldset class="grupo">
        <legend>Preço até <span id="rotulo-preco">${
          precoCurto(estado.precoMax ?? opcoes.precoMax) ?? 'qualquer'}</span></legend>
        <input type="range" name="precoMax" min="${PRECO_PISO}" max="${opcoes.precoMax}"
               step="${PRECO_PASSO}" value="${estado.precoMax ?? opcoes.precoMax}"
               aria-label="Preço máximo">
      </fieldset>
      <fieldset class="grupo">
        <legend>Quartos</legend>
        <div class="chips">
          ${[1, 2, 3, 4].map((n) => `
            <label class="chip">
              <input type="radio" name="quartos" value="${n}" ${estado.quartos === n ? 'checked' : ''}>
              ${n}${n === 4 ? '+' : ''}
            </label>`).join('')}
        </div>
      </fieldset>
      <fieldset class="grupo">
        <legend>Entrega</legend>
        <div class="chips">
          ${opcoes.anos.map((a) => `
            <label class="chip">
              <input type="radio" name="anoEntrega" value="${a}" ${estado.anoEntrega === a ? 'checked' : ''}>
              ${a}
            </label>`).join('')}
        </div>
      </fieldset>
      ${grupoChips('bairros', opcoes.bairros, 'Bairro', estado.bairros)}
      ${grupoChips('construtoras', opcoes.construtoras, 'Construtora', estado.construtoras)}
      ${grupoChips('diferenciais', opcoes.diferenciais, 'Diferenciais', estado.diferenciais)}
      <fieldset class="grupo">
        <legend>Disponibilidade</legend>
        <div class="chips">
          <label class="chip">
            <input type="checkbox" name="mostrarVendidos"
                   ${estado.mostrarVendidos ? 'checked' : ''}>
            Mostrar vendidos${opcoes.vendidos ? ` (${opcoes.vendidos})` : ''}
          </label>
        </div>
      </fieldset>`;

    const rotuloPreco = elemento.querySelector('#rotulo-preco');
    const faixa = elemento.querySelector('input[name="precoMax"]');
    faixa.addEventListener('input', () => {
      rotuloPreco.textContent =
        Number(faixa.value) >= opcoes.precoMax ? 'qualquer' : precoCurto(Number(faixa.value));
    });

    // Sem redesenhar a cada clique: o estado visual dos chips vem do CSS
    // `:has(input:checked)`, e redesenhar destruiria o elemento que acabou de
    // receber o foco, jogando a navegação por teclado de volta ao começo.
    elemento.querySelectorAll('input').forEach((i) =>
      i.addEventListener('change', () => aoMudar(leElemento(estadoBusca))));
  }

  let estadoBusca = '';
  return {
    desenha,
    defineBusca(b) { estadoBusca = b; },
  };
}
